import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { getSupplier, getSupplierFromUserId, selectSupplier, selectSupplierState } from '../../store/entities/supplier';
import { supplierCarListActions } from '../../store/entities/supplierCarList';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { SpzInput } from '../common/form/SpzInput';
import { SubmitButton } from '../common/form/SubmitButton';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { hasRole, isExternal } from '../../utils/roles';
import { selectIdentity } from '../../store/entities/identity';
import { SupplierCarCreateUpdate } from '@api/models/suppliers/SupplierCarCreateUpdate';
import { ChangeInfo } from '@api/models/suppliers/ChangeInfo';
import { formatChangeInfo } from '../../utils/format';
import { supplierCarService } from '@api/services/services';
import { useNavigation } from '../../utils/urlPaths';
import { SupplierCarTypeSelect } from './SupplierCarTypeSelect';
import { QcsToggleButtonFields } from '../common/form/QcsToggleButtonFields';
import { Box, IconButton } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { CarTypeManagement } from './CarTypeManagement';
import { RightDrawer } from '../common/RightDrawer';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';
import { UserRole } from '@api/models/enums/UserRole';

export const SupplierCarDetail: FC = () => {
  const { t } = useTranslation();
  const { supplierId, supplierCarId } = useParams();
  const identity = useAppSelector(selectIdentity);
  const { goBack } = useNavigation();
  const supplier = useAppSelector(selectSupplier);
  const dispatch = useAppDispatch();
  const [supplierCar, setSupplierCar] = useState<SupplierCarCreateUpdate>({});
  const [accessAllowedChanged, setAccessAllowedChanged] = useState<ChangeInfo | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [carTypeRefreshTrigger, setCarTypeRefreshTrigger] = useState(0);

  useEffect(() => {
    isExternal(identity) ? dispatch(getSupplierFromUserId(identity.id)) : dispatch(getSupplier(supplierId!));

    if (supplierCarId === 'new') return;

    supplierCarService.getSupplierCarById(supplierCarId!).then((car) => {
      const carDto = car.data;
      setSupplierCar({
        id: carDto.id,
        spz: carDto.spz,
        type: carDto.type?.id,
        accessAllowed: carDto.accessAllowed,
        lastEnteredBy: carDto.lastEnteredBy?.id,
        note: carDto.note,
      });
      setAccessAllowedChanged(carDto.accessAllowedChanged);
    });
  }, [supplierCarId, supplierId, dispatch, identity.roles, identity.id, identity]);

  const handleSubmit = async (data: SupplierCarCreateUpdate) => {
    if (supplierCarId === 'new') {
      await supplierCarService.createSupplierCar(supplier.id!, data);
    } else {
      await supplierCarService.updateSupplierCar(supplierCarId!, data);
    }

    dispatch(supplierCarListActions.reload(true));
    goBack();
  };

  return (
    <ShowResourceContent
      fetchStateSelector={selectSupplierState}
      resource={[resources.getSupplierFromUserId, resources.getSupplier, resources.getSupplierCarById]}
    >
      <DetailHeader title="supplier.car.detailTitle" showBackTo />
      <FormikDebounce<SupplierCarCreateUpdate>
        initialValues={{
          ...supplierCar,
          accessAllowed: supplierCar.accessAllowed ?? true,
        }}
        validateOnChange={true}
        validationSchema={Yup.object().shape({
          spz: validations.stringRequired(t),
          type: validations.stringRequired(t),
        })}
        onSubmit={handleSubmit}
      >
        <Form>
          <FormContainer>
            <SpzInput name="spz" required supplierId={supplierId} checkForDuplications />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <SupplierCarTypeSelect required name="type" label={t('supplier.car.type')} fullWidth refreshTrigger={carTypeRefreshTrigger} />
              </Box>
              {hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.AdminCompany, UserRole.AdminQcs]) && (
                <IconButton onClick={() => setDrawerOpen(true)} sx={{ mt: 1 }} aria-label={t('supplier.car.manageCarTypes')}>
                  <SettingsIcon />
                </IconButton>
              )}
            </Box>

            <Box>
              <QcsToggleButtonFields
                disabled={isExternal(identity)}
                name="accessAllowed"
                options={[
                  { value: true, label: t('supplier.car.allowEntry') },
                  { value: false, label: t('supplier.car.denyEntry') },
                ]}
              />
              {accessAllowedChanged?.at && (
                <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', mt: -1.5 }}>
                  {t('common.lastChange')}: {formatChangeInfo(accessAllowedChanged)}
                </Box>
              )}
            </Box>

            <Input name="note" label={t('supplier.car.note')} maxLength={250} multiline={true} rows={4} />

            <ShowError resource={[resources.createSupplierCar, resources.updateSupplierCar, resources.getSupplierCarById]} />
            <SubmitButton />
          </FormContainer>
        </Form>
      </FormikDebounce>

      <RightDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <CarTypeManagement onCarTypeUpdated={() => setCarTypeRefreshTrigger((prev) => prev + 1)} />
      </RightDrawer>
    </ShowResourceContent>
  );
};
